/**
 * `/workspace/:workspaceId/studio` — Studio / Client Mode console (moat plan §5).
 * Team & clients · branding · estimate · proposal · client page · handover.
 * Same header shell as `$view.tsx`; every card talks to the routes in docs/moat/CONTRACT.md "Phase 3".
 */
import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { AppHeader } from '#/components/layout/app-header'
import { BrandingCard } from '#/components/studio/branding-card'
import { ClientLinkCard, HandoverCard } from '#/components/studio/delivery-cards'
import { EstimateCard } from '#/components/studio/estimate-card'
import { MembersCard } from '#/components/studio/members-card'
import { ProposalCard } from '#/components/studio/proposal-card'
import { StudioUpsell } from '#/components/studio/studio-upsell'
import { useWorkspacePlans } from '#/lib/api'
import { useMembers } from '#/lib/api-studio'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/workspace/$workspaceId/studio')({
  validateSearch: z.object({ plan: z.string().optional() }),
  component: StudioPage,
})

function StudioPage() {
  const { workspaceId } = Route.useParams()
  const { plan: planParam } = Route.useSearch()
  const navigate = useNavigate()
  const { data: ws } = useWorkspacePlans(workspaceId)
  // same plan resolution as $view.tsx: ?plan= or the latest completed plan in the workspace
  const planId = planParam ?? [...(ws?.plans ?? [])].reverse().find((p) => p.status === 'completed')?.id ?? ws?.plans.at(-1)?.id
  useEffect(() => {
    if (!planParam && planId) {
      navigate({ to: '/workspace/$workspaceId/studio', params: { workspaceId }, search: { plan: planId }, replace: true })
    }
  }, [planParam, planId, workspaceId, navigate])

  const { data: members } = useMembers(workspaceId)
  const [gated, setGated] = useState(false)

  return (
    <>
      <AppHeader
        showStats={false}
        compact
        left={
          <nav className="hidden items-center gap-1 truncate text-sm text-muted-foreground lg:flex">
            <span>/</span>
            <Link to="/workspace/$workspaceId" params={{ workspaceId }} className="truncate hover:text-foreground">
              {ws?.workspace.name ?? '…'}
            </Link>
            <span>/</span>
            <span className="truncate text-foreground">{m.st_page_title()}</span>
          </nav>
        }
      />
      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">{m.st_page_title()}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{m.st_page_desc()}</p>
          </div>
          {gated ? <StudioUpsell /> : null}

          <MembersCard workspaceId={workspaceId} onGated={(code) => { if (code === 'studio_only') setGated(true) }} />
          <BrandingCard workspaceId={workspaceId} branding={members?.branding} fallbackName={ws?.workspace.name} />

          {planId ? (
            <>
              <EstimateCard planId={planId} />
              <ProposalCard planId={planId} />
              <ClientLinkCard planId={planId} />
              <HandoverCard planId={planId} onGated={() => setGated(true)} />
            </>
          ) : (
            <p className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">{m.st_no_plan()}</p>
          )}
        </div>
      </main>
    </>
  )
}
